import React from 'react';
import Select from 'react-select';

export const BaseSelect = ({
  options,
  value,
  onChange,
  placeholder = 'Select an option',
  isClearable = true,
  isDisabled = false,
  hasError = false,
  formatOptionLabel,
  className,
  menuMaxHeight = '300px',
  ...otherProps
}) => {
  const customStyles = {
    control: (provided, state) => {
      let borderColor = 'var(--Gray-300, #ccd5df)';
      let hoverBorderColor = 'var(--Gray-400, #96a2b5)';

      if (hasError) {
        borderColor = 'var(--Red-500, #ff4d4f)';
        hoverBorderColor = 'var(--Red-500, #ff4d4f)';
      } else if (state.isFocused) {
        borderColor = 'var(--Primary-500, #3b82f6)';
        hoverBorderColor = 'var(--Primary-500, #3b82f6)';
      }

      return {
        ...provided,
        minHeight: '44px',
        border: `1px solid ${borderColor}`,
        borderRadius: '8px',
        boxShadow: '0px 1px 2px 0px rgba(14, 24, 41, 0.05)',
        backgroundColor: isDisabled ? '#f9fafb' : 'var(--White, #fff)',
        transition: 'border-color 0.2s ease',
        '&:hover': {
          borderColor: hoverBorderColor,
          cursor: 'pointer',
        },
      };
    },
    option: (provided, state) => {
      let backgroundColor = '#ffffff';
      let color = '#374151';

      if (state.isSelected) {
        backgroundColor = 'var(--Secondary-100, #3b82f6)';
        color = '#000';
      } else if (state.isFocused) {
        backgroundColor = '#f1f5f9';
      }

      return {
        ...provided,
        backgroundColor,
        color,
        padding: '12px 16px',
        margin: '2px 0',
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: state.isSelected ? 'var(--Secondary-100, #3b82f6)' : '#f1f5f9',
        },
      };
    },
    menu: (provided) => ({
      ...provided,
      border: '1px solid var(--Gray-300, #ccd5df)',
      borderRadius: '8px',
      boxShadow: '0px 1px 2px 0px rgba(14, 24, 41, 0.05)',
      zIndex: 9999,
    }),
    menuList: (provided) => ({
      ...provided,
      padding: '4px 0',
      maxHeight: menuMaxHeight,
    }),
    singleValue: (provided) => ({
      ...provided,
      color: 'var(--Gray-700, #314155)',
      fontSize: '14px',
    }),
    placeholder: (provided) => ({
      ...provided,
      color: 'var(--Gray-500, #647187)',
      fontSize: '14px',
    }),
    input: (provided) => ({
      ...provided,
      color: 'var(--Gray-700, #314155)',
      fontSize: '14px',
      padding: 0,
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: '8px 14px',
    }),
    indicatorSeparator: (provided) => ({
      ...provided,
      display: 'none',
    }),
    clearIndicator: (provided) => ({
      ...provided,
      color: 'var(--Gray-500, #647187)',
      '&:hover': {
        color: 'var(--Gray-400, #96a2b5)',
      },
    }),
    dropdownIndicator: (provided, state) => ({
      ...provided,
      color: 'var(--Gray-500, #647187)',
      display: isDisabled ? 'none' : 'flex',
      '&:hover': {
        color: 'var(--Gray-400, #96a2b5)',
      },
      transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : null,
    }),
  };

  const handleChange = (selectedOption) => {
    if (onChange) {
      onChange(selectedOption);
    }
  };

  return (
    <Select
      options={options}
      value={value}
      onChange={handleChange}
      placeholder={placeholder}
      formatOptionLabel={formatOptionLabel}
      isClearable={isClearable}
      isDisabled={isDisabled}
      className={className}
      styles={customStyles}
      classNamePrefix="boon-select"
      {...otherProps}
    />
  );
};
